import {
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import { IStorageService } from "@shared/utils/IStorageService";
import { clearStoreTokens, setStoreTokens } from "@shared/utils/misc";
import pRetry from "p-retry";
import { getIsDemoQueryArgs, getStore } from "@shared/common/utils";
import { QueryReturnValue } from "@reduxjs/toolkit/query/react";
import {
  MOBILE_APP_USER_AGENT_KEY,
  MOBILE_APP_USER_AGENT_VALUE,
  REQUESTS_AUTH_HEADER,
  USER_AGENT_HEADER,
  WHOP_REQUESTS_AUTH_HEADER,
} from "@shared/common/constants";

export const getErrorStatus = (error: any): number | string | undefined => {
  if (!error || typeof error !== "object") return;
  const status = "originalStatus" in error ? error.originalStatus : error.status;
  if (typeof status !== "number" && typeof status !== "string") return;
  const normalizedStatus = Number(status);
  return Number.isNaN(normalizedStatus) ? status : normalizedStatus;
};

const refreshToken = async () => {
  const refreshToken = sharedLib.storageService.getRefreshToken();
  const { dispatch } = sharedLib.store;
  if (refreshToken) {
    return await dispatch(
      sharedLib.sharedApi.authentication.endpoints.refreshToken.initiate({
        refresh_token: refreshToken,
      })
    ).catch(() => {});
  }
};

export const refreshAndPersistToken = async (
  ignoreClear?: boolean,
  isWhop?: boolean
): Promise<string | undefined> => {
  let updatedToken: string | undefined;

  try {
    const response = await refreshToken();
    if (response && "data" in response) {
      updatedToken = response.data?.token;
      setStoreTokens(updatedToken);
    } else if (getErrorStatus(response?.error) === 401 && !ignoreClear) {
      if (isWhop) {
        sharedLib.store.dispatch(
          sharedLib.slices.common.actions.setShowWhopAuthErrorPage(true)
        );
      } else {
        clearStoreTokens();
      }
    }
  } catch (e) {}

  return updatedToken;
};

const getIsBetaUser = () => {
  try {
    const realUser = sharedLib.selectors.authentication.selectRealUser(
      getStore()
    );
    return !!realUser?.is_beta;
  } catch (e) {}
};

const getDemoUrl = (baseUrl: string) => baseUrl.replace("/app", "/demo");

export const buildBaseQuery = (
  storageService: IStorageService,
  baseUrl: string,
  getUserAgent?: () => Promise<string>,
  isWebView?: boolean,
  betaBaseUrl?: string,
  isWhop?: boolean,
  whopDevAuthToken?: string | null,
  whopExperienceId?: string | null
) => {
  const getBaseQuery = (baseUrl: string) => {
    return fetchBaseQuery({
      baseUrl,
      prepareHeaders: async (headers) => {
        const token = storageService.getAccessToken();
        if (token && !headers.get(REQUESTS_AUTH_HEADER)) {
          headers.set(
            isWhop ? WHOP_REQUESTS_AUTH_HEADER : REQUESTS_AUTH_HEADER,
            `token ${token}`
          );
        }
        if (getUserAgent) {
          headers.set(USER_AGENT_HEADER, await getUserAgent());
        }
        if (isWebView) {
          headers.set(MOBILE_APP_USER_AGENT_KEY, MOBILE_APP_USER_AGENT_VALUE);
        }
        if (whopDevAuthToken) {
          headers.set("X-Whop-User-Token", whopDevAuthToken);
        }
        if (whopExperienceId) {
          headers.set("X-Experience-Id", whopExperienceId);
        }

        return headers;
      },
    });
  };

  const userBaseQuery = getBaseQuery(baseUrl);
  const demoBaseQuery = getBaseQuery(getDemoUrl(baseUrl));
  const userBaseBetaQuery = getBaseQuery(betaBaseUrl || baseUrl);
  const demoBaseBetaQuery = getBaseQuery(getDemoUrl(betaBaseUrl || baseUrl));

  const resultHasCode = (
    result:
      | QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
      | undefined,
    code: number
  ): boolean => {
    return getErrorStatus(result?.error) === code;
  };

  const resultHasAuthError = (
    result:
      | QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
      | undefined
  ): boolean => resultHasCode(result, 401);

  const shouldSkipAuthRetry = (args: string | FetchArgs): boolean =>
    typeof args !== "string" &&
    args.url === "create_account_from_invite/" &&
    !!args.body?.user_auth_token;

  const withRetry: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const runBaseQuery = async () => {
      const isBetaUser = getIsBetaUser();
      const endpointUrl = typeof args === "string" ? args : args.url;
      let baseQuery = isBetaUser ? userBaseBetaQuery : userBaseQuery;
      let newArgs = args;
      const isUserQueryArgs =
        typeof args !== "string" && args.body?.is_demo_query === false;
      const isDemoQueryArgs =
        typeof args !== "string" && getIsDemoQueryArgs(args.body, endpointUrl);
      if (!isUserQueryArgs && isDemoQueryArgs) {
        baseQuery = isBetaUser ? demoBaseBetaQuery : demoBaseQuery;
      }
      if (
        typeof args !== "string" &&
        args.body &&
        !(args.body instanceof FormData)
      ) {
        const updatedBody = { ...args.body };
        delete updatedBody["is_demo_query"];
        newArgs = { ...args, body: updatedBody };
      }
      return baseQuery(newArgs, api, extraOptions);
    };

    const NUM_RETRIES = 3;

    return await pRetry(
      async (numRetry) => {
        let result = await runBaseQuery();
        if (resultHasAuthError(result) && !shouldSkipAuthRetry(args)) {
          if (
            typeof args !== "string" &&
            args?.url?.includes("refresh_token/")
          ) {
            if (isWhop) {
              sharedLib.store.dispatch(
                sharedLib.slices.common.actions.setShowWhopAuthErrorPage(true)
              );
            } else {
              clearStoreTokens();
            }
          } else if (await refreshAndPersistToken(false, isWhop)) {
            result = await runBaseQuery();
          }
        } else if (
          typeof args !== "string" &&
          (args.url?.startsWith("get_") || args.url === "symbols/") &&
          result?.error?.status === "FETCH_ERROR" &&
          numRetry < NUM_RETRIES + 1
        ) {
          throw new Error();
        }
        return result;
      },
      {
        factor: 2,
        minTimeout: 500,
        retries: NUM_RETRIES,
      }
    );
  };

  return withRetry;
};
